import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Gem, Clock, CheckCircle2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

// Phase 3 — minimal queue list (Phase 4 will add the per-tag classification UI).
export default function ColourStoneVerificationQueue() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const canCreate = ['admin', 'owner', 'stock_supervisor'].includes(currentUser?.role);

  const [tab, setTab] = useState('draft');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // ── Non-diamond tag start modal (Option C) ─────────────────────────────
  const [showFab, setShowFab] = useState(false);
  const [fabTag, setFabTag] = useState('');
  const [fabSubmitting, setFabSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/colour-stone-verification', { params: { status: tab } });
      setItems(r.data.items);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const submitFab = async () => {
    const tagNumber = fabTag.trim();
    if (!tagNumber) {
      toast.error('Enter a tag number');
      return;
    }
    setFabSubmitting(true);
    try {
      const r = await api.post('/colour-stone-verification/from-erp-tag', { tag_number: tagNumber });
      toast.success(`Tag ${tagNumber} — CSV draft created`);
      setShowFab(false);
      setFabTag('');
      navigate(`/colour-stone-verification/${r.data.csv_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create CSV');
    } finally {
      setFabSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50/40 via-rose-50/30 to-white">
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-gray-100">
        <div className="px-3 py-2.5 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-lg hover:bg-gray-50" data-testid="csv-queue-back">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <Gem className="w-4 h-4 text-amber-600" />
            <h1 className="text-sm font-bold text-gray-800">Colour Stone Verification</h1>
          </div>
          <div className="w-9" />
        </div>
        <div className="flex border-t border-gray-100">
          {[
            { k: 'draft',    label: 'Pending',  icon: Clock },
            { k: 'verified', label: 'Verified', icon: CheckCircle2 },
          ].map(t => {
            const Icon = t.icon;
            return (
              <button key={t.k} onClick={() => setTab(t.k)} data-testid={`csv-tab-${t.k}`}
                className={`flex-1 py-2.5 text-sm font-bold flex items-center justify-center gap-1.5 ${tab === t.k ? 'text-emerald-700 border-b-2 border-emerald-600 bg-emerald-50/40' : 'text-gray-500'}`}>
                <Icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="px-3 py-4 max-w-4xl mx-auto pb-28">
        {loading ? <div className="text-center py-12 text-gray-400 text-sm">Loading…</div>
          : items.length === 0 ? <div className="text-center py-12 text-gray-400 text-sm">No {tab === 'draft' ? 'pending' : 'verified'} records.</div>
          : (
            <div className="space-y-2">
              {items.map(c => {
                const unclassified = c.entries.filter(e => !e.combination_id).length;
                return (
                  <button
                    type="button"
                    key={c.id}
                    onClick={() => navigate(`/colour-stone-verification/${c.id}`)}
                    className="w-full text-left bg-white rounded-xl border border-gray-100 shadow-sm p-3 hover:border-emerald-200 hover:shadow-md transition-all"
                    data-testid={`csv-row-${c.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-bold text-gray-800 flex items-center gap-2">
                          Tag {c.tag_number}
                          {c.no_diamond_tag && (
                            <span className="text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider">
                              Non-diamond
                            </span>
                          )}
                        </p>
                        <p className="text-[11px] text-gray-500">{c.entries.length} pill(s) · {c.branch || 'no branch'}</p>
                      </div>
                      <div className="text-right">
                        {tab === 'draft' ? (
                          <span className="text-[11px] font-bold text-amber-700 bg-amber-50 px-2 py-1 rounded-full">
                            {unclassified} pending
                          </span>
                        ) : (
                          <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full">
                            ✓ Verified
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-[10px] text-emerald-600 font-bold mt-2">
                      Tap to {tab === 'draft' ? 'classify pills' : 'view details'} →
                    </p>
                  </button>
                );
              })}
            </div>
          )}
      </div>

      {/* ── Floating Action Button — Start a non-diamond tag (Option C) ── */}
      {canCreate && (
        <button
          type="button"
          onClick={() => setShowFab(true)}
          data-testid="csv-fab-new"
          className="fixed bottom-6 right-6 z-30 w-14 h-14 rounded-full bg-amber-600 text-white shadow-xl flex items-center justify-center active:bg-amber-700 hover:scale-105 transition-transform"
          aria-label="Start a non-diamond tag"
        >
          <Plus className="w-6 h-6" />
        </button>
      )}

      {/* FAB modal */}
      {showFab && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-3" data-testid="csv-fab-modal">
          <div className="bg-white w-full max-w-sm rounded-2xl shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <div>
                <p className="text-sm font-bold text-gray-900">Start a non-diamond tag</p>
                <p className="text-[11px] text-gray-500">Skip Diamond Verification</p>
              </div>
              <button onClick={() => { setShowFab(false); setFabTag(''); }} data-testid="csv-fab-close">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-[12px] text-gray-600 leading-snug">
                Use this only when the tag has <strong>zero diamonds</strong>. The
                tag must be physically at HO. We&apos;ll create a CSV draft and skip DV.
              </p>
              <label className="block">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Tag Number
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={fabTag}
                  onChange={e => setFabTag(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !fabSubmitting) submitFab(); }}
                  placeholder="e.g. 12345678901"
                  className="mt-1 w-full px-3 py-2.5 border border-gray-200 rounded-xl text-base font-mono focus:outline-none focus:border-amber-500"
                  data-testid="csv-fab-tag-input"
                  autoFocus
                />
              </label>
              <button
                type="button"
                onClick={submitFab}
                disabled={!fabTag.trim() || fabSubmitting}
                data-testid="csv-fab-submit"
                className="w-full py-3 rounded-xl bg-amber-600 text-white font-bold text-sm disabled:opacity-50 active:bg-amber-700"
              >
                {fabSubmitting ? 'Creating…' : 'Create CSV Draft →'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
